/**
 * Photo-first identity verification flow.
 *
 * Thin wrapper around `useDocumentVerificationStep` that wires it to the
 * identity document submit path (verificationService.submitAndVerifyIdentity +
 * confirmExtractedData). Keeps the legacy API shape so existing callers
 * (identity.tsx) don't need to change.
 */
import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { notification, NotificationType } from '../utils/haptics'
import { verificationService } from '@/lib/services/verification.service'
import type { IdDocumentType, PhotoSubmitResult } from '@/lib/types/verification.types'
import { ServiceError } from '@/lib/errors/service-error'
import { formatSvDui, isCompleteIsoDate } from '@/lib/utils/sv-identity'
import {
  identityRegistryFor,
  identityRequiresDateOfBirth,
  identityRequiresName,
  isPlausibleRegistryNumber,
} from '@/lib/data/identity-registries'
import { useDocumentVerificationStep } from './use-document-verification-step'
import { identityKeys } from './use-identity'
import { verificationKeys } from './use-verification'
import { authKeys } from './use-auth'

interface Options {
  userId: string | null
  documentType: IdDocumentType
  issuingCountry: string
  /** Provincia o estado. Solo lo llevan los registros sub-nacionales (US/CA). */
  issuingRegion?: string
  /** If true, the flow captures front + back; if false (e.g. passport), front only. */
  requireBack?: boolean
  /** If false, only store captured images in state; caller submits later. */
  autoSubmit?: boolean
  /**
   * Si el envío inicial es SOLO OCR (sin consultar el registro). La consulta
   * queda para el confirm, una vez que la persona vio y corrigió lo extraído.
   */
  extractOnly?: boolean
}

export function useIdentityVerification(opts: Options) {
  const idRef = useRef<string | null>(null)
  const confirmationRef = useRef<Promise<PhotoSubmitResult | null> | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)

  const step = useDocumentVerificationStep({
    requireBack: opts.requireBack !== false,
    autoSubmit: opts.autoSubmit !== false,
    submit: useCallback(
      async ({ frontImageUri, backImageUri }) => {
        if (!opts.userId) {
          throw new Error('Not authenticated')
        }
        idRef.current = null
        return verificationService
          .submitAndVerifyIdentity(opts.userId, {
            documentType: opts.documentType,
            issuingCountry: opts.issuingCountry,
            issuingRegion: opts.issuingRegion,
            frontImageUri,
            backImageUri: backImageUri ?? undefined,
            extractOnly: opts.extractOnly,
          })
          .then((result) => {
            idRef.current = result.identityDocumentId ?? result.recordId ?? null
            return result
          })
      },
      [opts.userId, opts.documentType, opts.issuingCountry, opts.issuingRegion, opts.extractOnly],
    ),
    // Re-run against the ALREADY-persisted row: same evidence, no recapture.
    // The explicit user action that follows a lost provider response.
    resumeSubmit: useCallback(
      async (recordId: string) => {
        if (!opts.userId) {
          throw new Error('Not authenticated')
        }
        const result = await verificationService.resumeIdentitySubmission(opts.userId, recordId)
        if (!result) {
          throw new ServiceError({
            message: 'The verification record no longer exists',
            code: 'VERIFICATION_FAILED',
            statusCode: 404,
            userMessageKey: 'verification.error_failed',
          })
        }
        idRef.current = result.identityDocumentId ?? result.recordId ?? null
        return result
      },
      [opts.userId],
    ),
  })

  const queryClient = useQueryClient()

  /**
   * Hydrate from a persisted submission: record the row id so a later
   * confirm reuses it, then let the step machine show the extracted fields.
   */
  const hydrate = useCallback(
    (
      submission: Parameters<ReturnType<typeof useDocumentVerificationStep>['hydrate']>[0],
      phase: 'extracted' | 'resume' = 'extracted',
    ) => {
      idRef.current = submission.recordId
      step.hydrate(submission, phase)
    },
    [step],
  )

  const persistConfirmation = useCallback(async () => {
    if (!opts.userId || !idRef.current) return null
    const extracted = step.state.extracted
    const hasNames = Boolean(extracted.firstName?.trim() && extracted.lastName?.trim())
    const hasDateOfBirth = Boolean(
      extracted.dateOfBirth?.trim() && isCompleteIsoDate(extracted.dateOfBirth),
    )
    // La forma del número la decide el registro del país, no el tipo de
    // documento. Estaba escrito `opts.documentType === 'dui'`, que era el país
    // disfrazado: no había otro registro. Una CURP mexicana tiene 18 caracteres
    // con letras, y con la regla vieja pasaba solo por no estar vacía.
    const registry =
      opts.documentType === 'passport' ? null : identityRegistryFor(opts.issuingCountry)
    const hasDocumentNumber = isPlausibleRegistryNumber(registry, extracted.documentNumber)

    // La fecha de nacimiento se exige donde el registro la pide, y eso es por
    // JURISDICCIÓN, no por país: la CURP ya la lleva codificada dentro, Florida
    // y Ontario consultan con el número solo, y Kansas pide los cuatro campos.
    // Preguntarle al país exigía la fecha a Florida y a Ontario, que la
    // rechazaban antes de llegar a consultar.
    const dateOfBirthRequired = identityRequiresDateOfBirth(registry, opts.issuingRegion)
    // El nombre solo se exige donde es parte de la identidad o de la consulta:
    // sin registro (pasaporte) o Kansas. Para DUI/CURP/cédula, la consulta va
    // por número y fecha — exigir el nombre bloquearía a alguien cuyo nombre el
    // OCR no leyó por un reflejo, aunque el número y la fecha estén perfectos.
    const nameRequired = identityRequiresName(registry, opts.issuingRegion)

    if (
      (nameRequired && !hasNames) ||
      (dateOfBirthRequired && !hasDateOfBirth) ||
      !hasDocumentNumber
    ) {
      throw new ServiceError({
        message: 'Identity review fields are incomplete',
        code: 'VALIDATION_ERROR',
        statusCode: 400,
        userMessageKey: 'verification.review_required_fields',
      })
    }

    const normalizedDocumentNumber =
      registry?.kind === 'dui'
        ? formatSvDui(extracted.documentNumber ?? '')
        : (extracted.documentNumber?.trim() ?? null)

    const result = await verificationService.confirmExtractedData(opts.userId, idRef.current, {
      firstName: extracted.firstName ?? null,
      lastName: extracted.lastName ?? null,
      dateOfBirth: extracted.dateOfBirth ?? null,
      documentNumber: normalizedDocumentNumber,
    })
    if (result) {
      step.applyResult(result)
    } else {
      step.markConfirmed()
    }
    // The photo-first path invalidated nothing at all, so a person could finish
    // verifying here and be told to verify by the payout gate, which caches the
    // answer for five minutes. The realtime channel would eventually catch it;
    // "eventually" is not what the screen that navigates next needs.
    if (opts.userId) {
      void queryClient.invalidateQueries({ queryKey: identityKeys.all })
      void queryClient.invalidateQueries({ queryKey: verificationKeys.overview(opts.userId) })
      // Same convergence as the realtime channel: the DB trigger derives
      // profiles.is_verified from this row change — the badge must not wait
      // out its 10-minute profile staleTime.
      void queryClient.invalidateQueries({ queryKey: authKeys.profile(opts.userId) })
    }

    if (!result || result.status === 'approved') {
      notification(NotificationType.Success)
    } else if (result.status === 'declined') {
      notification(NotificationType.Warning)
    }
    return result
  }, [opts.userId, opts.documentType, opts.issuingCountry, opts.issuingRegion, step, queryClient])

  // The shared Button's eight-second circuit breaker is not a network lock.
  // Keep one promise for the complete operation, including direct callers.
  const confirmAndPersist = useCallback(() => {
    if (confirmationRef.current) return confirmationRef.current
    setIsConfirming(true)
    const pending = persistConfirmation().finally(() => {
      confirmationRef.current = null
      setIsConfirming(false)
    })
    confirmationRef.current = pending
    return pending
  }, [persistConfirmation])

  const capture = (action: () => Promise<void>) => async () => {
    if (confirmationRef.current) return
    await action()
  }

  return {
    state: step.state,
    isConfirming,
    captureFront: capture(step.captureFront),
    captureBack: capture(step.captureBack),
    retakeFront: capture(step.retakeFront),
    retakeBack: capture(step.retakeBack),
    updateField: (...args: Parameters<typeof step.updateField>) => {
      if (!confirmationRef.current) step.updateField(...args)
    },
    confirmAndPersist,
    resume: step.resume,
    hydrate,
    reset: () => {
      if (confirmationRef.current) return
      idRef.current = null
      step.reset()
    },
  }
}
